import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import useSecurityOperationStatus from '../components/watchlist_table/useSecurityOperationStatus';
import {
    WATCHLIST_DELTA_CLASSIFICATIONS,
    WATCHLIST_CLIENT_PUBLICATION_REQUIRED_FIELD,
    requiresWatchlistClientPublication,
    WATCHLIST_REVISION_PROGRESS_EVENT,
    WATCHLIST_REVISION_PROGRESS_OBSERVED_EVENT,
    isSecurityOperationEventForScope,
    normalizeSecurityOperationSessionScope,
    parseWatchlistDeltaClassification,
    scopeSecurityOperationEventDetail,
} from '../utils/securityResolutionEvents';
import {
    requirePreparedWatchlistPublication,
    parseWatchlistPublicationReceipt,
    WATCHLIST_PUBLICATION_DISPOSITIONS,
} from '../utils/watchlistPublication';
import { normalizeWatchlistLifecycle } from '../utils/watchlistLifecycle';
import { emitWatchlistClientMetric } from '../utils/watchlistObservability';
import {
    parseWatchlistAcceptedRevisionSignal,
    WATCHLIST_ACCEPTED_REVISION_SIGNAL_REFRESH_EVENT,
} from '../utils/watchlistAcceptedRevisionSignal';

export const WATCHLIST_PUBLICATION_LIFECYCLES = Object.freeze({
    IDLE: 'idle',
    PROCESSING: 'processing',
    PUBLISHING_DATABASE: 'publishing_database',
    PUBLISHING_CLIENT: 'publishing_client',
    COMMITTED: 'committed',
    UNCHANGED: 'unchanged',
    NEEDS_ATTENTION: 'needs_attention',
    FAILED: 'failed',
    CANCELLED: 'cancelled',
    SUPERSEDED: 'superseded',
});

const CLIENT_PUBLICATION_STATES = Object.freeze({
    IDLE: 'idle',
    WAITING: 'waiting',
    PUBLISHING: 'publishing',
    COMMITTED: 'committed',
});

export const DEFAULT_CLIENT_PUBLICATION_HANDOFF_TIMEOUT_MS = 10 * 1000;
export const DEFAULT_ACCEPTED_REVISION_POLL_INTERVAL_MS = 60 * 1000;

const TERMINAL_FAILURE_STATUSES = new Set(['failed', 'stale']);
const TERMINAL_CANCELLED_STATUSES = new Set(['cancelled']);
const TERMINAL_SUPERSEDED_STATUSES = new Set(['superseded']);
const TERMINAL_SUCCESS_STATUSES = new Set([
    'succeeded',
]);
const MAX_TRACKED_PUBLICATION_KEYS = 64;
const MAX_OBSOLETE_PUBLICATION_RETRIES = 1;

function publicationFailure(error) {
    const status = Number(error?.status);
    if (Number.isInteger(status) && status >= 400 && status <= 599) {
        return { classification: 'http', httpStatus: status };
    }
    if (error?.backendTransportFailure === true) {
        return { classification: 'transport', httpStatus: null };
    }
    return { classification: 'contract', httpStatus: null };
}

const canRetryFailure = failure => failure?.classification === 'transport'
    || (failure?.classification === 'http' && failure.httpStatus >= 500);

function discardPreparedBundle(prepared) {
    try { prepared.discard(); } catch (_error) {
        console.warn('Could not discard the obsolete prepared Watchlist view.');
    }
}

function canCommitPreparedBundle(prepared) {
    const current = prepared.canCommit();
    if (typeof current !== 'boolean') {
        if (current && typeof current.then === 'function') void Promise.resolve(current).catch(() => {});
        throw new Error('Prepared Watchlist guard must return a synchronous boolean.');
    }
    return current;
}


const boundedPercent = (value) => {
    const numeric = Number(value);
    if (!Number.isFinite(numeric)) return 0;
    return Math.max(0, Math.min(100, numeric));
};

const publicationKey = (receipt) => (
    receipt ? `${receipt.revisionSeq}:${receipt.revisionId}` : ''
);

export function deriveWatchlistPublicationLifecycle(detail) {
    const normalized = normalizeWatchlistLifecycle(detail);
    const status = normalized.status;
    const phase = normalized.phase;
    if (status) {
        if (TERMINAL_SUPERSEDED_STATUSES.has(status)) {
            return WATCHLIST_PUBLICATION_LIFECYCLES.SUPERSEDED;
        }
        if (TERMINAL_CANCELLED_STATUSES.has(status)) {
            return WATCHLIST_PUBLICATION_LIFECYCLES.CANCELLED;
        }
        if (TERMINAL_FAILURE_STATUSES.has(status)) {
            return WATCHLIST_PUBLICATION_LIFECYCLES.FAILED;
        }
        if (status === 'unchanged') {
            return WATCHLIST_PUBLICATION_LIFECYCLES.UNCHANGED;
        }
        if (TERMINAL_SUCCESS_STATUSES.has(status)) {
            return WATCHLIST_PUBLICATION_LIFECYCLES.PUBLISHING_CLIENT;
        }
    }
    if (phase === 'publishing' || phase === 'complete') {
        return WATCHLIST_PUBLICATION_LIFECYCLES.PUBLISHING_DATABASE;
    }
    return WATCHLIST_PUBLICATION_LIFECYCLES.PROCESSING;
}

function initialCoordinatorState({ userUid = '', sessionScopeToken = '' } = {}) {
    return {
        userUid: String(userUid || '').trim(),
        sessionScopeToken: normalizeSecurityOperationSessionScope(sessionScopeToken),
        userGeneration: 0,
        revisionGeneration: 0,
        revisionId: null,
        revisionSeq: 0,
        key: '',
        operationId: null,
        operationType: null,
        serverStatus: null,
        serverPhase: null,
        lifecycle: WATCHLIST_PUBLICATION_LIFECYCLES.IDLE,
        clientPublicationState: CLIENT_PUBLICATION_STATES.IDLE,
        progressPercent: 0,
        detail: null,
        attemptCount: 0,
        error: null,
        failure: null,
        deltaClassification: null,
        activePublicationAttemptId: null,
    };
}

// Samples of one revision are merged so fields that only one source carries
// (an Apply response's mutation delta or operation type) survive, but the
// newest lifecycle must win.  An Add or Security Resolution apply opens with
// its Apply response, whose nested `lifecycle` is the running sample, while
// later status samples arrive flat.  Left in place, that nested lifecycle is
// what normalizeWatchlistLifecycle reads, so the Dashboard was handed a
// running operation without its terminal receipt and never published it.
function mergeObservedDetail(previous, next, lifecycle) {
    const merged = { ...previous, ...next };
    if (merged.lifecycle !== undefined) merged.lifecycle = lifecycle;
    return merged;
}

export function watchlistPublicationCoordinatorReducer(state, action) {
    switch (action.type) {
        case 'user_reset': {
            const next = initialCoordinatorState({
                userUid: action.userUid,
                sessionScopeToken: action.sessionScopeToken,
            });
            next.userGeneration = state.userGeneration + 1;
            return next;
        }
        case 'contract_error':
            return {
                ...state,
                lifecycle: WATCHLIST_PUBLICATION_LIFECYCLES.NEEDS_ATTENTION,
                clientPublicationState: CLIENT_PUBLICATION_STATES.IDLE,
                error: action.error,
                failure: { classification: 'contract', httpStatus: null },
            };
        case 'observe': {
            const detail = action.detail && typeof action.detail === 'object'
                ? action.detail
                : {};
            const receipt = parseWatchlistPublicationReceipt(detail);
            if (!receipt) return state;
            const key = publicationKey(receipt);
            if (
                receipt.revisionSeq < state.revisionSeq
                || (
                    receipt.revisionSeq === state.revisionSeq
                    && state.revisionId
                    && state.revisionId !== receipt.revisionId
                )
            ) {
                return state;
            }
            const sameRevision = state.key === key;
            if (
                sameRevision
                && state.clientPublicationState === CLIENT_PUBLICATION_STATES.COMMITTED
            ) {
                return state;
            }
            const normalized = normalizeWatchlistLifecycle(detail);
            const rawLifecycle = deriveWatchlistPublicationLifecycle(normalized);
            const terminalSuccess = (
                rawLifecycle === WATCHLIST_PUBLICATION_LIFECYCLES.PUBLISHING_CLIENT
            );
            const classificationResult = action.classificationResult
                || (terminalSuccess
                    ? parseWatchlistDeltaClassification(detail)
                    : { classification: null, error: null });
            const membershipChanged = (
                classificationResult.classification
                === WATCHLIST_DELTA_CLASSIFICATIONS.MEMBERSHIP_CHANGED
            );
            // An obligation declared by any sample of this revision holds:
            // the terminal status sample comes from polling and never repeats
            // what the opening event declared.
            const publicationRequiredByObservation = (
                membershipChanged
                || requiresWatchlistClientPublication(detail)
                || (sameRevision && requiresWatchlistClientPublication(state.detail))
            );
            const observedLifecycle = terminalSuccess
                ? publicationRequiredByObservation
                    ? WATCHLIST_PUBLICATION_LIFECYCLES.PUBLISHING_CLIENT
                    : classificationResult.error
                        ? WATCHLIST_PUBLICATION_LIFECYCLES.NEEDS_ATTENTION
                        : WATCHLIST_PUBLICATION_LIFECYCLES.UNCHANGED
                : rawLifecycle;
            const alreadyPublishingClient = sameRevision && (
                state.lifecycle === WATCHLIST_PUBLICATION_LIFECYCLES.PUBLISHING_CLIENT
                || state.lifecycle === WATCHLIST_PUBLICATION_LIFECYCLES.COMMITTED
            );
            const lifecycle = alreadyPublishingClient
                && (
                    observedLifecycle === WATCHLIST_PUBLICATION_LIFECYCLES.PROCESSING
                    || observedLifecycle === WATCHLIST_PUBLICATION_LIFECYCLES.PUBLISHING_DATABASE
                )
                ? state.lifecycle
                : observedLifecycle;
            const requiresClientPublication = (
                lifecycle === WATCHLIST_PUBLICATION_LIFECYCLES.PUBLISHING_CLIENT
            );
            const clientPublicationState = sameRevision
                && requiresClientPublication
                && [
                    CLIENT_PUBLICATION_STATES.WAITING,
                    CLIENT_PUBLICATION_STATES.PUBLISHING,
                ].includes(state.clientPublicationState)
                ? state.clientPublicationState
                : requiresClientPublication
                    ? CLIENT_PUBLICATION_STATES.WAITING
                    : CLIENT_PUBLICATION_STATES.IDLE;
            const observedProgress = requiresClientPublication
                ? 99
                : Math.min(99, boundedPercent(normalized.progress.percent));
            // A late nonterminal sample must not overwrite the terminal one.
            const keepsTerminalDetail = alreadyPublishingClient
                && lifecycle !== observedLifecycle;
            return {
                ...state,
                revisionGeneration: sameRevision
                    ? state.revisionGeneration
                    : state.revisionGeneration + 1,
                revisionId: receipt.revisionId,
                revisionSeq: receipt.revisionSeq,
                key,
                operationId: normalized.operation_id,
                operationType: String(detail.operation_type || '').trim() || null,
                serverStatus: normalized.status,
                serverPhase: normalized.phase,
                lifecycle,
                clientPublicationState,
                progressPercent: sameRevision
                    ? Math.max(state.progressPercent, observedProgress)
                    : observedProgress,
                detail: sameRevision
                    ? keepsTerminalDetail
                        ? state.detail
                        : mergeObservedDetail(state.detail, detail, normalized)
                    : detail,
                attemptCount: sameRevision ? state.attemptCount : 0,
                error: classificationResult.error
                    || (sameRevision ? state.error : null),
                failure: sameRevision ? state.failure : null,
                deltaClassification: classificationResult.classification,
                activePublicationAttemptId: sameRevision
                    ? state.activePublicationAttemptId
                    : null,
            };
        }
        case 'publication_retry_requested':
            if (
                state.lifecycle !== WATCHLIST_PUBLICATION_LIFECYCLES.NEEDS_ATTENTION
                || state.key !== action.key
                || state.userGeneration !== action.userGeneration
                || state.revisionGeneration !== action.revisionGeneration
                || state.attemptCount !== action.attemptCount
                || !canRetryFailure(state.failure)
            ) return state;
            return {
                ...state,
                lifecycle: WATCHLIST_PUBLICATION_LIFECYCLES.PUBLISHING_CLIENT,
                clientPublicationState: CLIENT_PUBLICATION_STATES.WAITING,
                error: null,
                failure: null,
            };
        case 'publication_started':
            if (
                state.key !== action.key
                || state.revisionGeneration !== action.revisionGeneration
                || ![
                    CLIENT_PUBLICATION_STATES.WAITING,
                ].includes(state.clientPublicationState)
            ) {
                return state;
            }
            return {
                ...state,
                clientPublicationState: CLIENT_PUBLICATION_STATES.PUBLISHING,
                attemptCount: state.attemptCount + 1,
                activePublicationAttemptId: action.attemptId,
                error: null,
            };
        case 'publication_needs_attention':
            if (
                state.key !== action.key
                || state.revisionGeneration !== action.revisionGeneration
                || state.activePublicationAttemptId !== action.attemptId
                || state.clientPublicationState === CLIENT_PUBLICATION_STATES.COMMITTED
            ) {
                return state;
            }
            return {
                ...state,
                lifecycle: WATCHLIST_PUBLICATION_LIFECYCLES.NEEDS_ATTENTION,
                clientPublicationState: CLIENT_PUBLICATION_STATES.IDLE,
                error: action.error,
                failure: action.failure,
                activePublicationAttemptId: null,
            };
        case 'publication_abandoned':
            if (
                state.key !== action.key
                || state.revisionGeneration !== action.revisionGeneration
                || state.activePublicationAttemptId !== action.attemptId
                || state.clientPublicationState !== CLIENT_PUBLICATION_STATES.PUBLISHING
            ) {
                return state;
            }
            return {
                ...state,
                clientPublicationState: CLIENT_PUBLICATION_STATES.WAITING,
                error: null,
                activePublicationAttemptId: null,
            };
        case 'publication_committed':
            if (
                state.key !== action.key
                || state.revisionGeneration !== action.revisionGeneration
                || state.activePublicationAttemptId !== action.attemptId
            ) {
                return state;
            }
            return {
                ...state,
                lifecycle: WATCHLIST_PUBLICATION_LIFECYCLES.COMMITTED,
                clientPublicationState: CLIENT_PUBLICATION_STATES.COMMITTED,
                progressPercent: 100,
                error: null,
                activePublicationAttemptId: null,
            };
        default:
            return state;
    }
}

function terminalProgressDetail(state, uiCommitComplete, sessionScopeToken) {
    const lifecycle = normalizeWatchlistLifecycle(state.detail);
    const terminalClassification = String(
        lifecycle?.terminal_receipt?.delta_classification || '',
    ).trim().toLowerCase();
    const mutationDelta = (
        state.detail?.watchlist_mutation_delta
        && typeof state.detail.watchlist_mutation_delta === 'object'
    )
        ? {
            ...state.detail.watchlist_mutation_delta,
            ...(terminalClassification
                ? { delta_classification: terminalClassification }
                : {}),
        }
        : state.detail?.watchlist_mutation_delta;
    const detail = {
        ...state.detail,
        ...lifecycle,
        ...(state.detail?.lifecycle ? { lifecycle } : {}),
        ...(terminalClassification
            ? { delta_classification: terminalClassification }
            : {}),
        ...(mutationDelta ? { watchlist_mutation_delta: mutationDelta } : {}),
    };
    return scopeSecurityOperationEventDetail(
        { ...detail, ui_commit_complete: uiCommitComplete },
        sessionScopeToken,
    );
}

function rememberBoundedKey(keySet, key) {
    keySet.add(key);
    while (keySet.size > MAX_TRACKED_PUBLICATION_KEYS) {
        const oldestKey = keySet.values().next().value;
        keySet.delete(oldestKey);
    }
}

function makePublicationReservation(key, sessionScopeToken) {
    return Object.freeze({
        type: 'watchlist_publication_reserved',
        key,
        sessionScopeToken,
    });
}

function makePublicationAttemptOwner(key, attemptId, sessionScopeToken) {
    return Object.freeze({
        type: 'watchlist_publication_attempt',
        key,
        attemptId,
        sessionScopeToken,
    });
}

export default function useWatchlistPublicationCoordinator({
    userUid,
    sessionScopeToken,
    enabled = true,
    clientHandoffTimeoutMs = DEFAULT_CLIENT_PUBLICATION_HANDOFF_TIMEOUT_MS,
    statusOptions = {},
    fetchAcceptedRevisionSignal = null,
    acceptedRevisionPollIntervalMs = DEFAULT_ACCEPTED_REVISION_POLL_INTERVAL_MS,
} = {}) {
    const normalizedUserUid = String(userUid || '').trim();
    const normalizedSessionScopeToken = normalizeSecurityOperationSessionScope(
        sessionScopeToken,
    );
    const scopeEnabled = Boolean(
        enabled && normalizedUserUid && normalizedSessionScopeToken,
    );
    const [state, dispatch] = useReducer(
        watchlistPublicationCoordinatorReducer,
        {
            userUid: normalizedUserUid,
            sessionScopeToken: normalizedSessionScopeToken,
        },
        initialCoordinatorState,
    );
    const [publisherGeneration, setPublisherGeneration] = useState(0);
    const [pendingReveal, setPendingReveal] = useState(null);
    const pendingRevealRef = useRef(null);
    const coordinatorMountedRef = useRef(true);
    const settlePendingReveal = useCallback((request, committed, error = null) => {
        if (!request || pendingRevealRef.current !== request || request.settled) return false;
        request.settled = true;
        request.revealed = committed;
        pendingRevealRef.current = null;
        // Keep ready100 painted through the synchronous reveal; exact commit
        // bookkeeping clears it in the same batch as the terminal watermark.
        if (!committed && coordinatorMountedRef.current) setPendingReveal(null);
        if (!committed) {
            discardPreparedBundle(request.prepared);
        }
        // Finish the exact commit in this synchronous acknowledgment, before
        // React may retire the publisher registration created by its own setters.
        // The Promise continuation must never reinterpret a completed reveal.
        if (committed) {
            try { request.onCommitted(); } catch (completionError) {
                request.reject(completionError);
                return false;
            }
        }
        if (error) request.reject(error);
        else request.resolve(committed);
        return committed;
    }, []);
    const discardPendingReveal = useCallback((entry = null) => {
        const request = pendingRevealRef.current;
        if (request && (!entry || request.entry === entry)) settlePendingReveal(request, false);
        if (coordinatorMountedRef.current) setPendingReveal(current => (
            !entry || (current?.key === entry.owner.key && current?.attemptId === entry.attemptId)
                ? null : current
        ));
    }, [settlePendingReveal]);
    const acknowledgeReveal = useCallback((presentation) => {
        const request = pendingRevealRef.current;
        if (!request || request.presentation !== presentation || request.committing) return false;
        try {
            if (!request.isCurrentAttempt() || !canCommitPreparedBundle(request.prepared)) {
                return settlePendingReveal(request, false);
            }
            request.committing = true;
            const committed = request.prepared.commit();
            if (committed !== true && committed !== false) {
                if (committed && typeof committed.then === 'function') {
                    void Promise.resolve(committed).catch(() => {});
                }
                throw new Error('Prepared Watchlist commit must return a synchronous boolean.');
            }
            return settlePendingReveal(request, committed);
        } catch (error) {
            return settlePendingReveal(request, false, error);
        }
    }, [settlePendingReveal]);
    const waitForPreparedReveal = useCallback((entry, prepared, isCurrentAttempt, details, onCommitted) => {
        try {
            if (pendingRevealRef.current) throw new Error('Another Watchlist reveal is pending.');
            if (!isCurrentAttempt() || !canCommitPreparedBundle(prepared)) {
                discardPreparedBundle(prepared);
                return Promise.resolve(false);
            }
        } catch (error) {
            discardPreparedBundle(prepared);
            throw error;
        }
        const request = { entry, prepared, isCurrentAttempt, onCommitted, settled: false, committing: false };
        request.promise = new Promise((resolve, reject) => { request.resolve = resolve; request.reject = reject; });
        request.presentation = Object.freeze({ ...details, isCurrent: () => {
            if (request.revealed) return isCurrentAttempt();
            if (pendingRevealRef.current !== request || request.settled || request.committing) return false;
            try { return isCurrentAttempt() && canCommitPreparedBundle(prepared); } catch (_error) { return false; }
        } });
        pendingRevealRef.current = request;
        setPendingReveal(request.presentation);
        return request.promise;
    }, []);

    const stateRef = useRef(state);
    const activeUserUidRef = useRef(normalizedUserUid);
    const publisherRef = useRef(null);
    const publicationAttemptSequenceRef = useRef(0);
    const ownershipScopeRef = useRef(normalizedSessionScopeToken);
    const acceptedRevisionRef = useRef({ revisionSeq: 0, revisionId: null, key: '' });
    const acceptedSignalWatermarkRef = useRef({
        initialized: false,
        revisionSeq: 0,
        revisionId: null,
    });
    const pendingAcceptedSignalRef = useRef(null);
    const inFlightByKeyRef = useRef(new Map());
    const projected99KeysRef = useRef(new Set());
    const committedKeysRef = useRef(new Set());
    const needsAttentionKeysRef = useRef(new Set());
    const exclusiveCoreLoadOwnerRef = useRef(null);

    useEffect(() => {
        coordinatorMountedRef.current = true;
        if (!pendingRevealRef.current) setPendingReveal(null);
        return () => {
            coordinatorMountedRef.current = false;
            discardPendingReveal();
            inFlightByKeyRef.current.forEach((entry) => {
                if (entry.handoffTimer) window.clearTimeout(entry.handoffTimer);
                entry.registration.active = false;
            });
            inFlightByKeyRef.current.clear();
            if (publisherRef.current) publisherRef.current.active = false;
            publisherRef.current = null;
            exclusiveCoreLoadOwnerRef.current = null;
        };
    }, [discardPendingReveal]);

    stateRef.current = state;
    activeUserUidRef.current = normalizedUserUid;

    if (ownershipScopeRef.current !== normalizedSessionScopeToken) {
        ownershipScopeRef.current = normalizedSessionScopeToken;
        discardPendingReveal();
        inFlightByKeyRef.current.forEach((entry) => {
            if (entry.handoffTimer) window.clearTimeout(entry.handoffTimer);
        });
        inFlightByKeyRef.current.clear();
        projected99KeysRef.current.clear();
        committedKeysRef.current.clear();
        needsAttentionKeysRef.current.clear();
        acceptedRevisionRef.current = { revisionSeq: 0, revisionId: null, key: '' };
        acceptedSignalWatermarkRef.current = {
            initialized: false,
            revisionSeq: 0,
            revisionId: null,
        };
        pendingAcceptedSignalRef.current = null;
        if (publisherRef.current) publisherRef.current.active = false;
        publisherRef.current = null;
        exclusiveCoreLoadOwnerRef.current = null;
    }

    useEffect(() => {
        if (
            state.userUid === normalizedUserUid
            && state.sessionScopeToken === normalizedSessionScopeToken
        ) return;
        discardPendingReveal();
        dispatch({
            type: 'user_reset',
            userUid: normalizedUserUid,
            sessionScopeToken: normalizedSessionScopeToken,
        });
        setPublisherGeneration((generation) => generation + 1);
    }, [
        discardPendingReveal,
        normalizedSessionScopeToken,
        normalizedUserUid,
        state.sessionScopeToken,
        state.userUid,
    ]);

    const observe = useCallback((detail) => {
        if (
            !scopeEnabled
            || !isSecurityOperationEventForScope(detail, normalizedSessionScopeToken)
        ) return;
        let normalized;
        try {
            normalized = normalizeWatchlistLifecycle(detail);
        } catch (error) {
            dispatch({
                type: 'contract_error',
                error: error instanceof Error
                    ? error.message
                    : 'Watchlist lifecycle is invalid.',
            });
            return;
        }
        // Pending reviews are drafts, not accepted publication operations.
        // History may observe their shared event, but it must not transfer the
        // coordinator's ownership or monotonic watermark away from active work.
        if (normalized.status === 'pending_review') return;
        const lifecycle = deriveWatchlistPublicationLifecycle(normalized);
        const terminalSuccess = (
            lifecycle === WATCHLIST_PUBLICATION_LIFECYCLES.PUBLISHING_CLIENT
        );
        const receipt = parseWatchlistPublicationReceipt(detail);
        if (!receipt) {
            if (terminalSuccess && !acceptedRevisionRef.current.key) {
                dispatch({
                    type: 'contract_error',
                    error: 'Terminal watchlist publication requires a valid revision receipt.',
                });
            }
            return;
        }
        const key = publicationKey(receipt);
        const accepted = acceptedRevisionRef.current;
        if (
            receipt.revisionSeq < accepted.revisionSeq
            || (
                receipt.revisionSeq === accepted.revisionSeq
                && accepted.revisionId
                && accepted.revisionId !== receipt.revisionId
            )
            || committedKeysRef.current.has(key)
            || needsAttentionKeysRef.current.has(key)
        ) return;

        const classificationResult = terminalSuccess
            ? parseWatchlistDeltaClassification(detail)
            : { classification: null, error: null };
        const current = stateRef.current;
        if (
            terminalSuccess
            && accepted.key === key
            && current.key === key
            && current.deltaClassification
            && (
                classificationResult.error
                || classificationResult.classification !== current.deltaClassification
            )
        ) return;

        const isNewRevision = accepted.key !== key;
        const inheritsUncommittedPublication = Boolean(
            isNewRevision && exclusiveCoreLoadOwnerRef.current,
        );
        const requiresClientPublication = (
            classificationResult.classification
            === WATCHLIST_DELTA_CLASSIFICATIONS.MEMBERSHIP_CHANGED
            || requiresWatchlistClientPublication(detail)
            || (current.key === key && requiresWatchlistClientPublication(current.detail))
            || inheritsUncommittedPublication
        );
        const observedDetail = inheritsUncommittedPublication
            ? {
                ...detail,
                [WATCHLIST_CLIENT_PUBLICATION_REQUIRED_FIELD]: true,
            }
            : detail;
        const sameRevisionNonterminalRegression = (
            !isNewRevision
            && exclusiveCoreLoadOwnerRef.current?.key === key
            && [
                WATCHLIST_PUBLICATION_LIFECYCLES.PROCESSING,
                WATCHLIST_PUBLICATION_LIFECYCLES.PUBLISHING_DATABASE,
            ].includes(lifecycle)
        );
        if (isNewRevision) {
            acceptedRevisionRef.current = {
                revisionSeq: receipt.revisionSeq,
                revisionId: receipt.revisionId,
                key,
            };
            inFlightByKeyRef.current.forEach((entry, inFlightKey) => {
                if (inFlightKey === key) return;
                if (entry.handoffTimer) window.clearTimeout(entry.handoffTimer);
                discardPendingReveal(entry);
                inFlightByKeyRef.current.delete(inFlightKey);
            });
        }
        if (terminalSuccess && requiresClientPublication) {
            if (isNewRevision || exclusiveCoreLoadOwnerRef.current?.key !== key) {
                exclusiveCoreLoadOwnerRef.current = makePublicationReservation(
                    key,
                    normalizedSessionScopeToken,
                );
            }
        } else if (
            !sameRevisionNonterminalRegression
            && (isNewRevision || exclusiveCoreLoadOwnerRef.current?.key === key)
        ) {
            exclusiveCoreLoadOwnerRef.current = null;
        }
        dispatch({ type: 'observe', detail: observedDetail, classificationResult });
    }, [discardPendingReveal, normalizedSessionScopeToken, scopeEnabled]);

    const statusOwner = useSecurityOperationStatus({
        ...statusOptions,
        userUid: normalizedUserUid,
        sessionScopeToken: normalizedSessionScopeToken,
        enabled: scopeEnabled,
    });

    useEffect(() => {
        if (!statusOwner.publicationObservation) return;
        observe(statusOwner.publicationObservation);
    }, [observe, statusOwner.publicationObservation]);

    const commitBootstrapRevision = useCallback((revision) => {
        if (!scopeEnabled) return false;
        const hasRevisionEnvelope = Boolean(
            revision
            && typeof revision === 'object'
            && Object.prototype.hasOwnProperty.call(revision, 'revisionId')
            && Object.prototype.hasOwnProperty.call(revision, 'revisionSeq'),
        );
        const revisionId = String(revision?.revisionId || '').trim();
        const revisionSeq = Number(revision?.revisionSeq ?? 0);
        const validEmptyRevision = !revisionId && revisionSeq === 0;
        if (
            !hasRevisionEnvelope
            || (
                !validEmptyRevision
                && (!revisionId || !Number.isSafeInteger(revisionSeq) || revisionSeq <= 0)
            )
        ) {
            dispatch({
                type: 'contract_error',
                error: 'Core Watchlist load requires a valid accepted revision stamp.',
            });
            return false;
        }

        const key = revisionId ? publicationKey({ revisionId, revisionSeq }) : '';
        // A core bootstrap can read the just-published database revision before
        // its terminal operation poll arrives. Its revision stamp does not
        // discharge an already accepted operation's client publication work.
        // Use the synchronous accepted receipt, rather than render state, so
        // an opening event in this same turn is protected too.
        const preserveAcceptedPublication = Boolean(
            key
            && acceptedRevisionRef.current.key === key
            && !committedKeysRef.current.has(key)
        );
        const watermark = acceptedSignalWatermarkRef.current;
        if (watermark.initialized) {
            if (revisionSeq < watermark.revisionSeq) return true;
            if (
                watermark.revisionSeq === revisionSeq
                && watermark.revisionId !== (revisionId || null)
            ) {
                dispatch({
                    type: 'contract_error',
                    error: 'Accepted Watchlist revision sequence changed identity.',
                });
                return false;
            }
            if (revisionSeq === watermark.revisionSeq) return true;
            acceptedSignalWatermarkRef.current = {
                initialized: true,
                revisionSeq,
                revisionId,
            };
            acceptedRevisionRef.current = { revisionId, revisionSeq, key };
            if (!preserveAcceptedPublication) {
                rememberBoundedKey(committedKeysRef.current, key);
            }
            return true;
        }

        const pending = pendingAcceptedSignalRef.current;
        acceptedSignalWatermarkRef.current = {
            initialized: true,
            revisionSeq,
            revisionId: revisionId || null,
        };
        if (revisionId) {
            acceptedRevisionRef.current = { revisionId, revisionSeq, key };
            if (!preserveAcceptedPublication) {
                rememberBoundedKey(committedKeysRef.current, key);
            }
        }
        pendingAcceptedSignalRef.current = null;
        if (!pending || pending.revisionSeq < revisionSeq) return true;
        if (pending.revisionSeq === revisionSeq) {
            if (revisionId && pending.revisionId !== revisionId) {
                dispatch({
                    type: 'contract_error',
                    error: 'Accepted Watchlist revision sequence changed identity.',
                });
                return false;
            }
            return true;
        }

        acceptedSignalWatermarkRef.current = {
            initialized: true,
            revisionSeq: pending.revisionSeq,
            revisionId: pending.revisionId,
        };
        if (pending.publicationObservation) {
            observe(scopeSecurityOperationEventDetail(
                pending.publicationObservation,
                normalizedSessionScopeToken,
            ));
        }
        return true;
    }, [normalizedSessionScopeToken, observe, scopeEnabled]);

    useEffect(() => {
        if (typeof window === 'undefined' || !scopeEnabled) {
            return undefined;
        }
        const handleObservation = (event) => observe(event?.detail);
        window.addEventListener(WATCHLIST_REVISION_PROGRESS_EVENT, handleObservation);
        window.addEventListener(WATCHLIST_REVISION_PROGRESS_OBSERVED_EVENT, handleObservation);
        return () => {
            window.removeEventListener(WATCHLIST_REVISION_PROGRESS_EVENT, handleObservation);
            window.removeEventListener(WATCHLIST_REVISION_PROGRESS_OBSERVED_EVENT, handleObservation);
        };
    }, [observe, scopeEnabled]);

    useEffect(() => {
        if (
            !scopeEnabled
            || typeof fetchAcceptedRevisionSignal !== 'function'
            || typeof window === 'undefined'
        ) {
            return undefined;
        }
        let active = true;
        let timeoutId = null;
        let pollInFlight = false;
        let refreshQueued = false;
        let failureReported = false;
        const intervalMs = Math.max(
            1,
            Number(acceptedRevisionPollIntervalMs)
                || DEFAULT_ACCEPTED_REVISION_POLL_INTERVAL_MS,
        );
        const schedule = () => {
            if (!active) return;
            if (timeoutId != null) window.clearTimeout(timeoutId);
            timeoutId = window.setTimeout(runPoll, intervalMs);
        };
        const runPoll = async () => {
            if (!active) return;
            if (pollInFlight) {
                refreshQueued = true;
                return;
            }
            pollInFlight = true;
            if (timeoutId != null) {
                window.clearTimeout(timeoutId);
                timeoutId = null;
            }
            try {
                const payload = await fetchAcceptedRevisionSignal();
                if (!active) return;
                const signal = parseWatchlistAcceptedRevisionSignal(payload);
                const watermark = acceptedSignalWatermarkRef.current;
                failureReported = false;
                if (!watermark.initialized) {
                    const pending = pendingAcceptedSignalRef.current;
                    if (
                        signal
                        && (
                            !pending
                            || signal.revisionSeq > pending.revisionSeq
                        )
                    ) {
                        pendingAcceptedSignalRef.current = signal;
                    } else if (
                        signal
                        && pending
                        && signal.revisionSeq === pending.revisionSeq
                        && signal.revisionId !== pending.revisionId
                    ) {
                        dispatch({
                            type: 'contract_error',
                            error: 'Accepted Watchlist revision sequence changed identity.',
                        });
                    }
                    return;
                }
                if (!signal || signal.revisionSeq < watermark.revisionSeq) return;
                if (signal.revisionSeq === watermark.revisionSeq) {
                    if (
                        watermark.revisionId
                        && signal.revisionId !== watermark.revisionId
                    ) {
                        dispatch({
                            type: 'contract_error',
                            error: 'Accepted Watchlist revision sequence changed identity.',
                        });
                    }
                    return;
                }
                acceptedSignalWatermarkRef.current = {
                    initialized: true,
                    revisionSeq: signal.revisionSeq,
                    revisionId: signal.revisionId,
                };
                if (signal.publicationObservation) {
                    observe(scopeSecurityOperationEventDetail(
                        signal.publicationObservation,
                        normalizedSessionScopeToken,
                    ));
                }
            } catch (error) {
                if (active && !failureReported) {
                    failureReported = true;
                    console.warn(
                        'Accepted Watchlist revision polling is temporarily unavailable.',
                        error,
                    );
                }
            } finally {
                pollInFlight = false;
                if (refreshQueued) {
                    refreshQueued = false;
                    void runPoll();
                } else {
                    schedule();
                }
            }
        };
        const handleRefresh = () => {
            void runPoll();
        };
        window.addEventListener(
            WATCHLIST_ACCEPTED_REVISION_SIGNAL_REFRESH_EVENT,
            handleRefresh,
        );
        void runPoll();
        return () => {
            active = false;
            if (timeoutId != null) window.clearTimeout(timeoutId);
            window.removeEventListener(
                WATCHLIST_ACCEPTED_REVISION_SIGNAL_REFRESH_EVENT,
                handleRefresh,
            );
        };
    }, [
        acceptedRevisionPollIntervalMs,
        fetchAcceptedRevisionSignal,
        normalizedSessionScopeToken,
        observe,
        scopeEnabled,
    ]);

    const scheduleRegistrationHandoff = useCallback((registration) => {
        if (!registration) return;
        inFlightByKeyRef.current.forEach((entry, key) => {
            if (entry.registration !== registration || entry.handoffTimer) return;
            entry.handoffTimer = window.setTimeout(() => {
                if (
                    registration.active
                    || inFlightByKeyRef.current.get(key) !== entry
                ) return;
                discardPendingReveal(entry);
                inFlightByKeyRef.current.delete(key);
                const current = stateRef.current;
                if (
                    ownershipScopeRef.current !== normalizedSessionScopeToken
                    || current.key !== key
                    || current.activePublicationAttemptId !== entry.attemptId
                ) return;
                emitWatchlistClientMetric({
                    stage: 'client_publication',
                    outcome: 'abandoned',
                    durationMs: Math.max(0, Date.now() - entry.startedAt),
                    attemptCount: Math.max(1, current.attemptCount),
                    failureClass: 'navigation_handoff',
                });
                dispatch({
                    type: 'publication_abandoned',
                    key,
                    revisionGeneration: entry.revisionGeneration,
                    attemptId: entry.attemptId,
                });
            }, Math.max(1, Number(clientHandoffTimeoutMs) || 1));
        });
    }, [clientHandoffTimeoutMs, discardPendingReveal, normalizedSessionScopeToken]);

    const deactivateRegistration = useCallback((registration) => {
        if (!registration || !registration.active) return;
        registration.active = false;
        inFlightByKeyRef.current.forEach((entry, key) => {
            if (entry.registration !== registration) return;
            discardPendingReveal(entry);
            if (exclusiveCoreLoadOwnerRef.current === entry.owner) {
                exclusiveCoreLoadOwnerRef.current = makePublicationReservation(
                    key,
                    normalizedSessionScopeToken,
                );
            }
        });
        scheduleRegistrationHandoff(registration);
    }, [discardPendingReveal, normalizedSessionScopeToken, scheduleRegistrationHandoff]);

    const registerPublisher = useCallback((publisher) => {
        if (typeof publisher !== 'function') {
            throw new TypeError('Watchlist publication executor must be a function.');
        }
        deactivateRegistration(publisherRef.current);
        const registration = {
            publisher,
            userUid: activeUserUidRef.current,
            sessionScopeToken: normalizedSessionScopeToken,
            active: true,
        };
        publisherRef.current = registration;
        setPublisherGeneration((generation) => generation + 1);
        return () => {
            deactivateRegistration(registration);
            if (publisherRef.current === registration) {
                publisherRef.current = null;
                setPublisherGeneration((generation) => generation + 1);
            }
        };
    }, [deactivateRegistration, normalizedSessionScopeToken]);

    useEffect(() => {
        inFlightByKeyRef.current.forEach((entry, key) => {
            if (!state.key || key === state.key) return;
            if (entry.handoffTimer) window.clearTimeout(entry.handoffTimer);
            discardPendingReveal(entry);
            inFlightByKeyRef.current.delete(key);
        });
        if (
            [
                WATCHLIST_PUBLICATION_LIFECYCLES.FAILED,
                WATCHLIST_PUBLICATION_LIFECYCLES.CANCELLED,
                WATCHLIST_PUBLICATION_LIFECYCLES.SUPERSEDED,
                WATCHLIST_PUBLICATION_LIFECYCLES.UNCHANGED,
            ].includes(state.lifecycle)
            && exclusiveCoreLoadOwnerRef.current?.key === state.key
        ) {
            exclusiveCoreLoadOwnerRef.current = null;
        }
    }, [discardPendingReveal, normalizedSessionScopeToken, state.key, state.lifecycle]);

    useEffect(() => {
        if (
            state.lifecycle !== WATCHLIST_PUBLICATION_LIFECYCLES.PUBLISHING_CLIENT
            || !state.key
            || projected99KeysRef.current.has(state.key)
        ) {
            return;
        }
        rememberBoundedKey(projected99KeysRef.current, state.key);
        window.dispatchEvent(new CustomEvent(WATCHLIST_REVISION_PROGRESS_EVENT, {
            detail: terminalProgressDetail(state, false, normalizedSessionScopeToken),
        }));
    }, [normalizedSessionScopeToken, state]);

    useEffect(() => {
        if (
            state.clientPublicationState !== CLIENT_PUBLICATION_STATES.WAITING
            || state.userUid !== normalizedUserUid
            || state.sessionScopeToken !== normalizedSessionScopeToken
            || !state.key
            || committedKeysRef.current.has(state.key)
            || inFlightByKeyRef.current.has(state.key)
        ) {
            return;
        }
        const registration = publisherRef.current;
        if (
            !registration
            || registration.userUid !== normalizedUserUid
            || registration.sessionScopeToken !== normalizedSessionScopeToken
        ) return;

        const key = state.key;
        const revisionGeneration = state.revisionGeneration;
        const userGeneration = state.userGeneration;
        const requestUserUid = normalizedUserUid;
        const requestSessionScopeToken = normalizedSessionScopeToken;
        const attemptId = publicationAttemptSequenceRef.current + 1;
        publicationAttemptSequenceRef.current = attemptId;
        const publicationDetail = terminalProgressDetail(
            state,
            false,
            normalizedSessionScopeToken,
        );
        const owner = makePublicationAttemptOwner(
            key,
            attemptId,
            normalizedSessionScopeToken,
        );
        const entry = {
            attempt: null,
            attemptId,
            startedAt: Date.now(),
            registration,
            revisionGeneration,
            handoffTimer: null,
            owner,
        };
        inFlightByKeyRef.current.set(key, entry);
        exclusiveCoreLoadOwnerRef.current = owner;
        const executionContext = Object.freeze({
            owner,
            isCurrent: () => (
                ownershipScopeRef.current === requestSessionScopeToken
                && activeUserUidRef.current === requestUserUid
                && inFlightByKeyRef.current.get(key) === entry
                && publisherRef.current === registration
                && registration.active
                && exclusiveCoreLoadOwnerRef.current === owner
            ),
        });
        const attempt = Promise.resolve().then(() => (
            registration.publisher(publicationDetail, executionContext)
        ));
        entry.attempt = attempt;
        dispatch({ type: 'publication_started', key, revisionGeneration, attemptId });

        attempt.then(async (result) => {
            const discardReturnedPrepared = () => {
                if (result?.disposition !== WATCHLIST_PUBLICATION_DISPOSITIONS.PREPARED
                    || typeof result.prepared?.discard !== 'function') return;
                discardPreparedBundle(result.prepared);
            };
            if (inFlightByKeyRef.current.get(key) !== entry) {
                discardReturnedPrepared();
                return;
            }
            const current = stateRef.current;
            const revisionIsCurrent = (
                activeUserUidRef.current === requestUserUid
                && ownershipScopeRef.current === requestSessionScopeToken
                && current.userGeneration === userGeneration
                && current.revisionGeneration === revisionGeneration
                && current.key === key
            );
            if (!revisionIsCurrent) {
                discardReturnedPrepared();
                discardPendingReveal(entry);
                inFlightByKeyRef.current.delete(key);
                if (entry.handoffTimer) window.clearTimeout(entry.handoffTimer);
                return;
            }
            if (!executionContext.isCurrent()) {
                discardReturnedPrepared();
                if (!registration.active && inFlightByKeyRef.current.get(key) === entry) {
                    return;
                }
                discardPendingReveal(entry);
                inFlightByKeyRef.current.delete(key);
                if (entry.handoffTimer) window.clearTimeout(entry.handoffTimer);
                dispatch({
                    type: 'publication_abandoned',
                    key,
                    revisionGeneration,
                    attemptId,
                });
                return;
            }
            if (result?.disposition !== WATCHLIST_PUBLICATION_DISPOSITIONS.OBSOLETE) {
                let prepared;
                try { prepared = requirePreparedWatchlistPublication(result); } catch (error) {
                    discardReturnedPrepared();
                    throw error;
                }
                const isCurrentAttempt = () => {
                    const latest = stateRef.current;
                    return executionContext.isCurrent()
                        && latest.userGeneration === userGeneration
                        && latest.revisionGeneration === revisionGeneration
                        && latest.key === key
                        && latest.lifecycle === WATCHLIST_PUBLICATION_LIFECYCLES.PUBLISHING_CLIENT;
                };
                const revealed = await waitForPreparedReveal(entry, prepared, isCurrentAttempt, {
                    key, attemptId, revisionId: current.revisionId, revisionSeq: current.revisionSeq,
                    userUid: requestUserUid, sessionScopeToken: requestSessionScopeToken,
                }, () => {
                    discardPendingReveal(entry);
                    inFlightByKeyRef.current.delete(key);
                    if (entry.handoffTimer) window.clearTimeout(entry.handoffTimer);
                    if (committedKeysRef.current.has(key)) return;
                    acceptedSignalWatermarkRef.current = {
                        initialized: true,
                        revisionSeq: current.revisionSeq,
                        revisionId: current.revisionId,
                    };
                    pendingAcceptedSignalRef.current = null;
                    rememberBoundedKey(committedKeysRef.current, key);
                    emitWatchlistClientMetric({
                        stage: 'client_publication',
                        outcome: 'succeeded',
                        durationMs: Math.max(0, Date.now() - entry.startedAt),
                        attemptCount: Math.max(1, current.attemptCount),
                    });
                    dispatch({
                        type: 'publication_committed',
                        key,
                        revisionGeneration,
                        attemptId,
                    });
                    window.dispatchEvent(new CustomEvent(WATCHLIST_REVISION_PROGRESS_EVENT, {
                        detail: terminalProgressDetail(
                            current,
                            true,
                            normalizedSessionScopeToken,
                        ),
                    }));
                    if (exclusiveCoreLoadOwnerRef.current === owner) {
                        exclusiveCoreLoadOwnerRef.current = null;
                    }
                });
                if (revealed) return;
                if (!isCurrentAttempt()) {
                    const ownsEntry = inFlightByKeyRef.current.get(key) === entry;
                    if (ownsEntry) {
                        discardPendingReveal(entry);
                        inFlightByKeyRef.current.delete(key);
                    }
                    if (entry.handoffTimer) window.clearTimeout(entry.handoffTimer);
                    if (ownsEntry && coordinatorMountedRef.current) {
                        dispatch({ type: 'publication_abandoned', key, revisionGeneration, attemptId });
                    }
                    return;
                }
                result = { disposition: WATCHLIST_PUBLICATION_DISPOSITIONS.OBSOLETE };
            }
            if (
                result?.disposition === WATCHLIST_PUBLICATION_DISPOSITIONS.OBSOLETE
                && current.attemptCount <= MAX_OBSOLETE_PUBLICATION_RETRIES
            ) {
                if (exclusiveCoreLoadOwnerRef.current === owner) {
                    exclusiveCoreLoadOwnerRef.current = makePublicationReservation(
                        key,
                        normalizedSessionScopeToken,
                    );
                }
                discardPendingReveal(entry);
                inFlightByKeyRef.current.delete(key);
                if (entry.handoffTimer) window.clearTimeout(entry.handoffTimer);
                emitWatchlistClientMetric({
                    stage: 'client_publication',
                    outcome: 'abandoned',
                    durationMs: Math.max(0, Date.now() - entry.startedAt),
                    attemptCount: Math.max(1, current.attemptCount),
                    failureClass: 'navigation_handoff',
                });
                dispatch({
                    type: 'publication_abandoned',
                    key,
                    revisionGeneration,
                    attemptId,
                });
                return;
            }
            throw new Error(
                `Watchlist publication executor returned ${result?.disposition || 'an invalid disposition'}.`,
            );
        }).catch((error) => {
            if (inFlightByKeyRef.current.get(key) !== entry) return;
            const attemptWasCurrent = executionContext.isCurrent();
            const current = stateRef.current;
            const revisionIsCurrent = (
                activeUserUidRef.current === requestUserUid
                && ownershipScopeRef.current === requestSessionScopeToken
                && current.userGeneration === userGeneration
                && current.revisionGeneration === revisionGeneration
                && current.key === key
            );
            if (!revisionIsCurrent || committedKeysRef.current.has(key)) {
                discardPendingReveal(entry);
                inFlightByKeyRef.current.delete(key);
                if (entry.handoffTimer) window.clearTimeout(entry.handoffTimer);
                return;
            }
            if (!attemptWasCurrent) {
                if (!registration.active && inFlightByKeyRef.current.get(key) === entry) {
                    return;
                }
                discardPendingReveal(entry);
                inFlightByKeyRef.current.delete(key);
                if (entry.handoffTimer) window.clearTimeout(entry.handoffTimer);
                return;
            }
            if (exclusiveCoreLoadOwnerRef.current === owner) {
                exclusiveCoreLoadOwnerRef.current = makePublicationReservation(
                    key,
                    normalizedSessionScopeToken,
                );
            }
            discardPendingReveal(entry);
            inFlightByKeyRef.current.delete(key);
            if (entry.handoffTimer) window.clearTimeout(entry.handoffTimer);
            const errorMessage = error instanceof Error ? error.message : String(error);
            const failure = publicationFailure(error);
            rememberBoundedKey(needsAttentionKeysRef.current, key);
            emitWatchlistClientMetric({
                stage: 'client_publication',
                outcome: 'terminal_failure',
                durationMs: Math.max(0, Date.now() - entry.startedAt),
                attemptCount: Math.max(1, current.attemptCount),
                failureClass: failure.classification,
            });
            dispatch({
                type: 'publication_needs_attention',
                key,
                revisionGeneration,
                attemptId,
                error: errorMessage,
                failure,
            });
        });
    }, [
        normalizedUserUid,
        normalizedSessionScopeToken,
        publisherGeneration,
        state,
        discardPendingReveal,
        waitForPreparedReveal,
    ]);

    useEffect(() => {
        const request = pendingRevealRef.current;
        if (request && !request.isCurrentAttempt()) discardPendingReveal(request.entry);
    }, [discardPendingReveal, pendingReveal, publisherGeneration, state]);

    const retryClientPublication = useCallback((expected) => {
        const current = stateRef.current;
        const registration = publisherRef.current;
        const reservation = exclusiveCoreLoadOwnerRef.current;
        if (
            current.lifecycle !== WATCHLIST_PUBLICATION_LIFECYCLES.NEEDS_ATTENTION
            || !canRetryFailure(current.failure)
            || current.revisionId !== expected?.revisionId
            || current.revisionSeq !== expected?.revisionSeq
            || current.attemptCount !== expected?.attemptCount
            || current.userUid !== normalizedUserUid
            || current.sessionScopeToken !== normalizedSessionScopeToken
            || activeUserUidRef.current !== normalizedUserUid
            || ownershipScopeRef.current !== normalizedSessionScopeToken
            || acceptedRevisionRef.current.key !== current.key
            || !needsAttentionKeysRef.current.has(current.key)
            || committedKeysRef.current.has(current.key)
            || inFlightByKeyRef.current.has(current.key)
            || pendingRevealRef.current
            || !registration?.active
            || registration.userUid !== normalizedUserUid
            || registration.sessionScopeToken !== normalizedSessionScopeToken
            || reservation?.key !== current.key
            || reservation.sessionScopeToken !== normalizedSessionScopeToken
            || deriveWatchlistPublicationLifecycle(current.detail)
                !== WATCHLIST_PUBLICATION_LIFECYCLES.PUBLISHING_CLIENT
        ) return false;
        // Re-enter the existing preparation path, retaining the reservation
        // and the child's sealed request or verified recovery receipt.
        needsAttentionKeysRef.current.delete(current.key);
        dispatch({
            type: 'publication_retry_requested',
            key: current.key,
            userGeneration: current.userGeneration,
            revisionGeneration: current.revisionGeneration,
            attemptCount: current.attemptCount,
        });
        return true;
    }, [normalizedSessionScopeToken, normalizedUserUid]);

    const publicationState = useMemo(() => ({
        lifecycle: state.lifecycle,
        revisionId: state.revisionId,
        revisionSeq: state.revisionSeq,
        operationId: state.operationId,
        operationType: state.operationType,
        progressPercent: state.progressPercent,
        publishing: (
            state.lifecycle === WATCHLIST_PUBLICATION_LIFECYCLES.PUBLISHING_CLIENT
            && state.clientPublicationState !== CLIENT_PUBLICATION_STATES.COMMITTED
        ),
        committed: state.clientPublicationState === CLIENT_PUBLICATION_STATES.COMMITTED,
        attemptCount: state.attemptCount,
        readyForReveal: pendingReveal != null,
        error: state.error,
        failure: state.failure,
        canRetry: state.lifecycle === WATCHLIST_PUBLICATION_LIFECYCLES.NEEDS_ATTENTION
            && canRetryFailure(state.failure),
    }), [pendingReveal, state]);

    const visibleStatusOwner = useMemo(() => {
        const statusDoc = statusOwner.statusDoc;
        const statusRevisionId = String(
            statusDoc?.revisionId || statusDoc?.watchlist_revision_id || '',
        ).trim();
        const committedRevisionIsVisible = Boolean(
            statusDoc
            && state.clientPublicationState === CLIENT_PUBLICATION_STATES.COMMITTED
            && state.revisionId
            && statusRevisionId === state.revisionId
        );
        if (!committedRevisionIsVisible) {
            return {
                statusDoc,
                overallStatus: statusOwner.overallStatus,
                active: statusOwner.active,
            };
        }
        return {
            statusDoc: {
                ...statusDoc,
                topLevelStatus: 'success',
                progressPercent: 100,
            },
            overallStatus: 'success',
            active: false,
        };
    }, [
        state.clientPublicationState,
        state.revisionId,
        statusOwner.active,
        statusOwner.overallStatus,
        statusOwner.statusDoc,
    ]);

    return {
        ...statusOwner,
        ...visibleStatusOwner,
        publicationState,
        pendingReveal,
        acknowledgeReveal,
        commitBootstrapRevision,
        registerPublisher,
        retryClientPublication,
        observe,
        exclusiveCoreLoadOwnerRef,
    };
}
